import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonProgressBar,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import {
  checkmarkCircle,
  documentTextOutline,
  linkOutline,
  openOutline,
  playCircleOutline,
  libraryOutline,
} from "ionicons/icons";

import Header from "src/components/Header";
import ReproductorRecurso from "src/components/ReproductorRecurso";
import {
  CategoriaRecurso,
  Recurso,
  formatearDuracion,
  listarCategorias,
  listarRecursos,
} from "src/services/recursos";

import "./Recursos.css";

interface Toast {
  show: boolean;
  msg: string;
  color?: string;
}

const iconoPorTipo = (recurso: Recurso) => {
  if (recurso.tipo === "DOCUMENTO") return documentTextOutline;
  if (recurso.tipo === "ENLACE") return linkOutline;
  return playCircleOutline;
};

const Recursos: React.FC = () => {
  const [categorias, setCategorias] = useState<CategoriaRecurso[]>([]);
  const [categoriaActiva, setCategoriaActiva] = useState<string>("");
  const [recursos, setRecursos] = useState<Recurso[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState<Recurso | null>(null);
  const [toast, setToast] = useState<Toast>({ show: false, msg: "" });

  /* Traemos TODOS los recursos visibles de una sola vez y filtramos en memoria.
     El volumen es chico (decenas de items) y así cambiar de apartado no dispara
     otra request: el usuario en el punto de servicio no paga datos por navegar. */
  const cargar = useCallback(async (mostrarSpinner = true) => {
    if (mostrarSpinner) setCargando(true);
    try {
      const [cats, items] = await Promise.all([listarCategorias(), listarRecursos()]);
      setCategorias(cats);
      setRecursos(items);
      setCategoriaActiva((previa) => {
        if (previa && cats.some((c) => c.clave === previa)) return previa;
        const conContenido = cats.find((c) => c.total > 0) || cats[0];
        return conContenido?.clave ?? "";
      });
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar.", color: "danger" });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const visibles = useMemo(
    () => recursos.filter((r) => !categoriaActiva || r.categoria_clave === categoriaActiva),
    [recursos, categoriaActiva]
  );

  /* El reproductor avisa cuando el backend confirmó avance; reflejamos el
     cambio en la lista sin volver a pedirla. */
  const actualizarProgreso = (id: number, porcentaje: number, completado: boolean) => {
    setRecursos((previos) =>
      previos.map((r) =>
        r.id === id
          ? {
              ...r,
              progreso: {
                segundos_vistos: r.progreso?.segundos_vistos ?? 0,
                ultima_posicion_seg: r.progreso?.ultima_posicion_seg ?? 0,
                porcentaje,
                completado,
              },
            }
          : r
      )
    );
  };

  const abrirRecurso = (recurso: Recurso) => {
    if (recurso.tipo === "VIDEO") {
      setAbierto(recurso);
      return;
    }
    // Documentos y enlaces se abren fuera de la app (visor del sistema).
    if (recurso.url_externa) {
      window.open(recurso.url_externa, "_blank", "noopener,noreferrer");
    }
  };

  const renderTarjeta = (recurso: Recurso) => {
    const progreso = recurso.progreso;
    const porcentaje = progreso?.porcentaje ?? 0;
    const completado = Boolean(progreso?.completado);

    return (
      <IonCard key={recurso.id} button onClick={() => abrirRecurso(recurso)}>
        <IonCardContent>
          <div className="recurso-item">
            <div className="recurso-miniatura">
              {recurso.url_poster ? (
                <img src={recurso.url_poster} alt="" loading="lazy" />
              ) : (
                <IonIcon icon={iconoPorTipo(recurso)} color="medium" size="large" />
              )}
              {recurso.duracion_seg ? (
                <span className="recurso-duracion">{formatearDuracion(recurso.duracion_seg)}</span>
              ) : null}
            </div>

            <div className="recurso-item-cuerpo">
              <p className="recurso-titulo">{recurso.titulo}</p>
              {recurso.descripcion && <p className="recurso-descripcion">{recurso.descripcion}</p>}

              <div className="recurso-meta">
                {Boolean(recurso.obligatorio) && !completado && (
                  <IonBadge color="warning">Obligatorio</IonBadge>
                )}
                {completado && (
                  <IonBadge color="success">
                    <IonIcon icon={checkmarkCircle} /> Completado
                  </IonBadge>
                )}
                {recurso.tipo !== "VIDEO" && (
                  <IonBadge color="medium">
                    <IonIcon icon={openOutline} /> {recurso.tipo === "DOCUMENTO" ? "Documento" : "Enlace"}
                  </IonBadge>
                )}
              </div>

              {recurso.tipo === "VIDEO" && porcentaje > 0 && !completado && (
                <IonProgressBar
                  className="recurso-progreso"
                  value={Math.min(porcentaje / 100, 1)}
                  color="primary"
                />
              )}
            </div>
          </div>
        </IonCardContent>
      </IonCard>
    );
  };

  return (
    <IonPage>
      <Header titulo="Recursos" />

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={async (e) => {
          await cargar(false);
          e.detail.complete();
        }}>
          <IonRefresherContent />
        </IonRefresher>

        {categorias.length > 1 && (
          <IonSegment
            scrollable
            value={categoriaActiva}
            onIonChange={(e) => setCategoriaActiva(String(e.detail.value ?? ""))}
          >
            {categorias.map((categoria) => (
              <IonSegmentButton key={categoria.clave} value={categoria.clave}>
                {categoria.nombre}
              </IonSegmentButton>
            ))}
          </IonSegment>
        )}

        {cargando ? (
          <div className="recurso-vacio">
            <IonSpinner name="crescent" />
          </div>
        ) : visibles.length === 0 ? (
          <div className="recurso-vacio">
            <IonIcon icon={libraryOutline} size="large" />
            <p>Todavía no hay contenido disponible para vos en esta sección.</p>
          </div>
        ) : (
          visibles.map(renderTarjeta)
        )}
      </IonContent>

      <IonModal isOpen={Boolean(abierto)} onDidDismiss={() => setAbierto(null)}>
        <IonHeader>
          <IonToolbar color="primary">
            <IonTitle>{abierto?.titulo}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setAbierto(null)}>Cerrar</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {abierto && (
            <>
              <ReproductorRecurso
                recurso={abierto}
                onProgreso={(porcentaje, completado) =>
                  actualizarProgreso(abierto.id, porcentaje, completado)
                }
              />
              <div className="ion-padding">
                <h2 className="recurso-titulo">{abierto.titulo}</h2>
                {abierto.duracion_seg ? (
                  <IonText color="medium">
                    <p>Duración: {formatearDuracion(abierto.duracion_seg)}</p>
                  </IonText>
                ) : null}
                {abierto.descripcion && <p>{abierto.descripcion}</p>}
              </div>
            </>
          )}
        </IonContent>
      </IonModal>

      <IonToast
        isOpen={toast.show}
        message={toast.msg}
        color={toast.color}
        duration={3500}
        onDidDismiss={() => setToast({ show: false, msg: "" })}
      />
    </IonPage>
  );
};

export default Recursos;
